<template>
	<v-card class="solid-mesh-panel" color="panel_sub">
		<v-card-text class="pa-2">
			<v-row dense class="details-wrapper">
				<v-col cols="12">
					<v-card class="solid-mesh-detail-inner" :class="{ 'solid-mesh-detail-view': !isEditing }" color="panel_sub">
						<v-card-text class="pa-2">
							<v-row dense>
								<v-col cols="12">
									<div class="text-caption text-grey">{{ panelTitle }}</div>
								</v-col>
							</v-row>

							<!-- Dimensions (Y hidden for pipe / retaining-wall) -->
							<v-row dense>
								<v-col cols="12">
									<div class="text-caption">{{ dimensionsLabel }}</div>
									<template v-if="isEditing">
										<div class="field-stack mt-2">
											<DistanceSettingInput :model-value="rawInputs.dimensionX" :label="dimensionXLabel" active
												:base-unit="typeConfig.x.baseUnit" :step="dimensionXStep" :max-value="dimensionXMax"
												:min-value="dimensionXStep" :decimal-length="DIMENSION_DECIMALS" density="compact" hide-details
												variant="outlined" @focus="onDimensionInputFocus" @blur="onDimensionInputBlur"
												@update:model-value="onDimensionXChange" />
											<DistanceSettingInput :model-value="rawInputs.dimensionZ" :label="dimensionZLabel" active
												:base-unit="typeConfig.z.baseUnit" :step="dimensionZStep" :max-value="dimensionZMax"
												:min-value="dimensionZStep" :decimal-length="DIMENSION_DECIMALS" density="compact" hide-details
												variant="outlined" @focus="onDimensionInputFocus" @blur="onDimensionInputBlur"
												@update:model-value="onDimensionZChange" />
										</div>
									</template>
									<template v-else>
										<div class="text-body-2 mt-1">
											<div>{{ dimensionXLabel }}: {{ formattedDimensionX }}</div>
											<div v-if="structureType === SIMPLE_STRUCTURE_TYPE.RETAINING_WALL && debugWallThickness">
												{{ dimensionYLabel }}: {{ formattedDimensionY }}
											</div>
											<div>{{ dimensionZLabel }}: {{ formattedDimensionZ }}</div>
										</div>
									</template>
								</v-col>
							</v-row>

							<!-- Two-point edit P1/P2 (editing mode) -->
							<template v-if="isEditing">
								<v-row dense>
									<v-col cols="12">
										<div class="text-caption">{{ $t(basisPointKey1) }}</div>
										<div class="basis-point-group mt-1">
											<DistanceSettingInput :model-value="rawInputs.p1x" label="X" :step="anchorPointStep" active
												:decimal-length="anchorPointDecimals" density="compact" hide-details variant="outlined"
												class="mb-2" @update:model-value="(val) => onPointInput('p1', 'x', val)" />
											<DistanceSettingInput :model-value="rawInputs.p1y" label="Y" :step="anchorPointStep" active
												:decimal-length="anchorPointDecimals" density="compact" hide-details variant="outlined"
												class="mb-2" @update:model-value="(val) => onPointInput('p1', 'y', val)" />
											<DistanceSettingInput v-if="structureType === SIMPLE_STRUCTURE_TYPE.PIPE"
												:model-value="rawInputs.p1z" label="Z" active :step="anchorPointStep"
												:decimal-length="anchorPointDecimals" density="compact" hide-details variant="outlined"
												@update:model-value="(val) => onPointInput('p1', 'z', val)" />
										</div>
									</v-col>
									<v-col cols="12">
										<div class="text-caption">{{ $t(basisPointKey2) }}</div>
										<div class="basis-point-group mt-1">
											<DistanceSettingInput :model-value="rawInputs.p2x" label="X" :step="anchorPointStep" active
												:decimal-length="anchorPointDecimals" density="compact" hide-details variant="outlined"
												class="mb-2" @update:model-value="(val) => onPointInput('p2', 'x', val)" />
											<DistanceSettingInput :model-value="rawInputs.p2y" label="Y" :step="anchorPointStep" active
												:decimal-length="anchorPointDecimals" density="compact" hide-details variant="outlined"
												class="mb-2" @update:model-value="(val) => onPointInput('p2', 'y', val)" />
											<DistanceSettingInput v-if="structureType === SIMPLE_STRUCTURE_TYPE.PIPE"
												:model-value="rawInputs.p2z" label="Z" active :step="anchorPointStep"
												:decimal-length="anchorPointDecimals" density="compact" hide-details variant="outlined"
												@update:model-value="(val) => onPointInput('p2', 'z', val)" />
										</div>
									</v-col>
								</v-row>
							</template>
							<!-- Gradient display (non-editing) -->
							<v-row dense v-if="!isEditing">
								<v-col cols="12">
									<div class="text-caption">{{ $t("GRADIENT_PERCENT") }}</div>
									<div class="text-body-2">{{ gradientDisplayPercent }}%</div>
								</v-col>
							</v-row>

							<!-- Gradient edit (editing mode) -->
							<template v-if="isEditing">
								<v-row dense>
									<v-col cols="12">
										<div class="text-caption">{{ $t("AZIMUTH") }}</div>
										<v-text-field :model-value="rawInputs.heading" type="number" :step="azimuthConstraint.step"
											:min="azimuthConstraint.min" :max="azimuthConstraint.max" density="compact" hide-details
											variant="outlined" suffix="°" @input="onHeadingInputEvent"
											@update:model-value="onHeadingModelValueChange" />
									</v-col>
								</v-row>
								<v-row dense>
									<v-col cols="12">
										<div class="text-caption">{{ $t("GRADIENT") }}</div>
										<NumericSettingInput :model-value="rawInputs.gradient" :step="0.01" :min-value="gradientMin"
											:max-value="gradientMax" :decimal-length="gradientDecimals" density="compact" hide-details
											variant="outlined" suffix="%" @update:model-value="onGradientChangeLocal" />
									</v-col>
								</v-row>
							</template>

							<!-- Transparency & Color -->
							<template v-if="isEditing">
								<v-row dense>
									<v-col cols="12">
										<div class="text-caption">{{ $t("TRANSPARENCY") }}</div>
										<v-text-field :model-value="rawInputs.transparency" type="number" :min="transparencyConstraint.min"
											:max="transparencyConstraint.max" :step="transparencyConstraint.step" density="compact"
											hide-details variant="outlined" suffix="%"
											@update:model-value="(v) => onTransparencyChangeLocal(v)" @input="onTransparencyInput" />
									</v-col>
								</v-row>
								<v-row dense>
									<v-col cols="12">
										<div class="text-caption">{{ $t("COLOR") }}</div>
										<div class="d-flex align-center gap-2">
											<v-menu :close-on-content-click="false" location="bottom start">
												<template v-slot:activator="{ props: menuProps }">
													<div class="color-preview mr-2 cursor-pointer" :style="{ backgroundColor: editColorString }"
														v-bind="menuProps" />
												</template>
												<v-card min-width="300">
													<v-color-picker v-model="pickerColor" mode="rgb" hide-inputs show-swatches
														@update:model-value="onPickerColorChange" />
												</v-card>
											</v-menu>
											<v-text-field :model-value="rawInputs.colorR" label="R" type="number" :min="colorConstraint.min"
												:max="colorConstraint.max" :step="colorConstraint.step" density="compact" hide-details
												variant="outlined" style="max-width: 70px" persistent-placeholder placeholder=""
												@update:model-value="(v) => onColorRChange(v)" @input="onColorRInput" />
											<v-text-field :model-value="rawInputs.colorG" label="G" type="number" :min="colorConstraint.min"
												:max="colorConstraint.max" :step="colorConstraint.step" density="compact" hide-details
												variant="outlined" style="max-width: 70px" persistent-placeholder placeholder=""
												@update:model-value="(v) => onColorGChange(v)" @input="onColorGInput" />
											<v-text-field :model-value="rawInputs.colorB" label="B" type="number" :min="colorConstraint.min"
												:max="colorConstraint.max" :step="colorConstraint.step" density="compact" hide-details
												variant="outlined" style="max-width: 70px" persistent-placeholder placeholder=""
												@update:model-value="(v) => onColorBChange(v)" @input="onColorBInput" />
										</div>
									</v-col>
								</v-row>
							</template>
							<template v-else>
								<v-row dense>
									<v-col cols="3">
										<div class="text-caption my-2">{{ $t("TRANSPARENCY") }}</div>
										<div class="text-body-2">
											{{ Math.round((solidMesh as Record<string, unknown>).shapeConfig
												? ((solidMesh as Record<string, unknown>).shapeConfig as Record<string, unknown>).color
													? (((solidMesh as Record<string, unknown>).shapeConfig as Record<string, unknown>).color as
														Record<string, number>).alpha * 100
													: 0
												: 0) }}%
										</div>
									</v-col>
									<v-col cols="9">
										<div class="text-caption my-2">{{ $t("COLOR") }}</div>
										<div class="d-flex align-center">
											<div class="color-preview mr-2" :style="{ backgroundColor: viewColorString }" />
											<div class="text-body-2">
												R: {{ viewColorR }}, G: {{ viewColorG }}, B: {{ viewColorB }}
											</div>
										</div>
									</v-col>
								</v-row>
							</template>
						</v-card-text>
					</v-card>
				</v-col>
			</v-row>
		</v-card-text>
	</v-card>
</template>

<script setup lang="ts">
import { SIMPLE_STRUCTURE_TYPE } from "@/models/objects/solidMesh/types";
import type { TSolidMeshRotationStamp } from "@/store/modules/solid-mesh";
import {
	type TGeometryResult,
	type TPipeTwoPointParams,
	type PlainCartesian3 as TPoint3,
	type TRetainingWallTwoPointParams,
	type TSolidMeshLocalEndpoints,
	computePipeP2FromGradient,
	pipeGradientPercentFromPoints,
	rotateSegmentXYByHeadingDelta,
	scaleSegmentToLength,
	verticalHeightFromSlopeHeight,
	wallGradientPercentFromRollDeg,
} from "earthbrain-viewer";
import { computed, reactive, ref, toRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import DistanceSettingInput from "../DistanceSettingInput.vue";
import NumericSettingInput from "../NumericSettingInput.vue";
import {
	clampGradientPercent,
	computeDragPoints,
	roundTo3,
} from "./composables/useDoublePointFormSync";
import {
	DIMENSION_DECIMALS,
	formatDimensionValue,
} from "./composables/useSolidMeshDimensionConfig";
import type { TSolidMeshCommonRawInputs } from "./composables/useSolidMeshEdit";
import {
	SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE,
	SIMPLE_STRUCTURE_DIMENSION_MAX,
	SIMPLE_STRUCTURE_EDIT_CONSTRAINTS,
	getMaxDecimals,
	normalizeSpinnerInput,
	toOptionalNumber,
	useDimensionValidation,
	useInputActivity,
	useSolidMeshEdit,
} from "./composables/useSolidMeshEdit";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
const props = defineProps<{
	solidMeshId: string;
	isEditing: boolean;
}>();

// ---------------------------------------------------------------------------
// Setup
// ---------------------------------------------------------------------------
const store = useStore();
const { t } = useI18n();

const common = useSolidMeshEdit(
	toRef(props, "solidMeshId"),
	toRef(props, "isEditing"),
	store,
	t,
);

const {
	editData,
	localAnchorPoint,
	isUpdatingPosition,
	anchorFetchTimeout,
	solidMesh,
	structureType,
	panelTitle,
	typeConfig,
	convertLength,
	dimensionXLabel,
	dimensionYLabel,
	dimensionZLabel,
	dimensionXStep,
	dimensionZStep,
	scaledDimensions,
	formattedDimensionX,
	formattedDimensionY,
	editColorString,
	pickerColor,
	isDraggingSolidMesh,
	onTransparencyChange,
	onPickerColorChange: baseOnPickerColorChange,
	onColorChange,
	fetchLocalAnchorPoint,
	loadCommonEditData,
	getColorString,
	getRotationDegrees,
	initCommonRawInputs,
} = common;

const dimensionsLabel = computed(() => t("DIMENSIONS"));

// Hidden debug flag
const debugWallThickness = computed(
	() =>
		!!(window as unknown as { debugFlag?: { wallThickness?: unknown } })
			.debugFlag?.wallThickness,
);

type TDoublePointKey = `p${1 | 2}${"x" | "y" | "z"}`;

interface TDoublePointRawInputs extends TSolidMeshCommonRawInputs {
	p1x: number | undefined;
	p1y: number | undefined;
	p1z: number | undefined;
	p2x: number | undefined;
	p2y: number | undefined;
	p2z: number | undefined;
	heading: number | undefined;
	gradient: number | undefined;
}

const rawInputs = reactive<TDoublePointRawInputs>({
	transparency: undefined,
	dimensionX: undefined,
	dimensionY: undefined,
	dimensionZ: undefined,
	colorR: undefined,
	colorG: undefined,
	colorB: undefined,
	anchorX: undefined,
	anchorY: undefined,
	anchorZ: undefined,
	p1x: undefined,
	p1y: undefined,
	p1z: undefined,
	p2x: undefined,
	p2y: undefined,
	p2z: undefined,
	heading: undefined,
	gradient: undefined,
});

const { onDimensionInputFocus, onDimensionInputBlur } = useInputActivity(500);

useDimensionValidation({
	rawInputs,
	isEditing: toRef(props, "isEditing"),
	structureType: toRef(common, "structureType"),
	store,
});

const doublePointData = reactive({
	points: { p1: { x: 0, y: 0, z: 0 }, p2: { x: 0, y: 0, z: 0 } },
	gradient: { value: 0 },
});
const gradientDisplayPercent = ref(0);
const headingAngleDeg = ref(0);
const isUpdatingPoints = ref(false);
/**
 * Monotonic user-edit generation. Bumped on every applicable form edit; async
 * viewer reads capture it before awaiting and discard their response when it
 * changed — a boolean can miss an edit that starts AND finishes mid-await.
 */
let editGeneration = 0;
/** Terrain-level Z for retaining-wall base reference (bottom edge). */
const retainingWallBaseZ = ref<{ p1: number; p2: number }>({ p1: 0, p2: 0 });
const retainingWallBaseZInitialized = ref(false);

// ---------------------------------------------------------------------------
// Computed
// ---------------------------------------------------------------------------
const basisPointKey1 = computed(() =>
	structureType.value === SIMPLE_STRUCTURE_TYPE.PIPE
		? "PIPE_BASIS_POINT_1"
		: "WALL_BASIS_POINT_1",
);
const basisPointKey2 = computed(() =>
	structureType.value === SIMPLE_STRUCTURE_TYPE.PIPE
		? "PIPE_BASIS_POINT_2"
		: "WALL_BASIS_POINT_2",
);

const formattedDimensionZ = computed(() => {
	const cfg = typeConfig.value;
	let val = Number(scaledDimensions.value.z);
	if (structureType.value === SIMPLE_STRUCTURE_TYPE.RETAINING_WALL) {
		const gradPct = props.isEditing
			? Number(doublePointData.gradient?.value) || 0
			: gradientDisplayPercent.value;
		val = verticalHeightFromSlopeHeight(val, gradPct);
	}
	return formatDimensionValue(val, cfg.z, convertLength);
});

const dimensionXMax =
	structureType.value === SIMPLE_STRUCTURE_TYPE.PIPE
		? SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE
		: SIMPLE_STRUCTURE_DIMENSION_MAX;
const dimensionZMax = SIMPLE_STRUCTURE_DIMENSION_MAX;
const gradientMax = SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE;
const gradientMin = -gradientMax;
const gradientDecimals = getMaxDecimals(gradientMax);
const anchorPointStep = SIMPLE_STRUCTURE_EDIT_CONSTRAINTS.anchorPoint.step;
const anchorPointDecimals =
	SIMPLE_STRUCTURE_EDIT_CONSTRAINTS.anchorPoint.decimalLength;
const azimuthConstraint = SIMPLE_STRUCTURE_EDIT_CONSTRAINTS.azimuth;
const transparencyConstraint = SIMPLE_STRUCTURE_EDIT_CONSTRAINTS.transparency;
const colorConstraint = SIMPLE_STRUCTURE_EDIT_CONSTRAINTS.colorChannel;

// View-mode color computed
const shapeColor = computed(() => {
	const sm = solidMesh.value as Record<string, unknown> | undefined;
	const config = sm?.shapeConfig as
		| { color: { red: number; green: number; blue: number; alpha: number } }
		| undefined;
	return config?.color ?? { red: 0, green: 0, blue: 0, alpha: 0 };
});
const viewColorString = computed(() => getColorString(shapeColor.value));
const viewColorR = computed(() => Math.round(shapeColor.value.red * 255));
const viewColorG = computed(() => Math.round(shapeColor.value.green * 255));
const viewColorB = computed(() => Math.round(shapeColor.value.blue * 255));

// ---------------------------------------------------------------------------
// Methods
// ---------------------------------------------------------------------------
function getSolidMesh() {
	return solidMesh.value as Record<string, unknown>;
}

// --- rawInputs change handlers ---
// DistanceSettingInput emits values in the axis base unit. Its clamp and decimal
// limit are applied to the displayed value, so on a feet site the same numeric
// limits are read as feet by design. These handlers only store and propagate.
function onDimensionXChange(val: number | undefined | null) {
	rawInputs.dimensionX = val ?? undefined;
	if (val === undefined || val === null) return;
	editData.dimensions.x = val;
	onDimensionsChange();
}

function onDimensionZChange(val: number | undefined | null) {
	rawInputs.dimensionZ = val ?? undefined;
	if (val === undefined || val === null) return;
	editData.dimensions.z = val;
	onDimensionsChange();
}

function onTransparencyChangeLocal(val: unknown) {
	const normalized = toOptionalNumber(val);
	rawInputs.transparency = normalized;
	if (normalized === undefined) return;
	const constrained = normalizeSpinnerInput(normalized, transparencyConstraint);
	rawInputs.transparency = constrained;
	editData.transparency = constrained;
	onTransparencyChange();
}

function onTransparencyInput(event) {
	const target = event.target;
	if (!target) return;
	if (target.value === "") {
		rawInputs.transparency = undefined;
		return;
	}
	const parsed = target.valueAsNumber;
	if (!Number.isFinite(parsed)) return;
	const normalized = normalizeSpinnerInput(parsed, transparencyConstraint);
	target.valueAsNumber = normalized;
	onTransparencyChangeLocal(normalized);
}

function onColorRChange(val: unknown) {
	const normalized = toOptionalNumber(val);
	rawInputs.colorR = normalized;
	if (normalized === undefined) return;
	const constrained = normalizeSpinnerInput(normalized, colorConstraint);
	rawInputs.colorR = constrained;
	editData.color.red = constrained;
	onColorChange();
}

function onColorRInput(event) {
	const target = event.target;
	if (!target) return;
	if (target.value === "") {
		rawInputs.colorR = undefined;
		return;
	}
	const parsed = target.valueAsNumber;
	if (!Number.isFinite(parsed)) return;
	const normalized = normalizeSpinnerInput(parsed, colorConstraint);
	target.valueAsNumber = normalized;
	onColorRChange(normalized);
}

function onColorGChange(val: unknown) {
	const normalized = toOptionalNumber(val);
	rawInputs.colorG = normalized;
	if (normalized === undefined) return;
	const constrained = normalizeSpinnerInput(normalized, colorConstraint);
	rawInputs.colorG = constrained;
	editData.color.green = constrained;
	onColorChange();
}

function onColorGInput(event) {
	const target = event.target;
	if (!target) return;
	if (target.value === "") {
		rawInputs.colorG = undefined;
		return;
	}
	const parsed = target.valueAsNumber;
	if (!Number.isFinite(parsed)) return;
	const normalized = normalizeSpinnerInput(parsed, colorConstraint);
	target.valueAsNumber = normalized;
	onColorGChange(normalized);
}

function onColorBChange(val: unknown) {
	const normalized = toOptionalNumber(val);
	rawInputs.colorB = normalized;
	if (normalized === undefined) return;
	const constrained = normalizeSpinnerInput(normalized, colorConstraint);
	rawInputs.colorB = constrained;
	editData.color.blue = constrained;
	onColorChange();
}

function onColorBInput(event) {
	const target = event.target;
	if (!target) return;
	if (target.value === "") {
		rawInputs.colorB = undefined;
		return;
	}
	const parsed = target.valueAsNumber;
	if (!Number.isFinite(parsed)) return;
	const normalized = normalizeSpinnerInput(parsed, colorConstraint);
	target.valueAsNumber = normalized;
	onColorBChange(normalized);
}

/** Shadow composable's onPickerColorChange to also sync rawInputs. */
function onPickerColorChange(val: unknown) {
	baseOnPickerColorChange(val);
	rawInputs.colorR = editData.color.red;
	rawInputs.colorG = editData.color.green;
	rawInputs.colorB = editData.color.blue;
}

// min/max クランプと小数桁の切り詰めは NumericSettingInput が担うため、ここでは値の反映のみ行う
function onGradientChangeLocal(val: number | undefined | null) {
	if (val === undefined || val === null) {
		rawInputs.gradient = undefined;
		return;
	}
	rawInputs.gradient = val;
	onGradientChange(val);
}

function normalizeHeadingInput(value: number): number {
	const result = normalizeSpinnerInput(value, azimuthConstraint);
	return result === 0 ? 0 : result;
}

function onHeadingModelValueChange(val: unknown) {
	const headingValue = toOptionalNumber(val);
	if (headingValue === undefined) return;
	const normalized = normalizeHeadingInput(headingValue);
	rawInputs.heading = normalized;
	onHeadingChange(normalized);
}

function onHeadingInputEvent(event: Event) {
	const target = event.target as HTMLInputElement | null;
	if (!target) return;
	if (target.value === "") {
		rawInputs.heading = undefined;
		return;
	}
	const parsed = target.valueAsNumber;
	if (!Number.isFinite(parsed)) return;
	const normalized = normalizeHeadingInput(parsed);
	target.valueAsNumber = normalized;
	rawInputs.heading = normalized;
	onHeadingChange(normalized);
}

/** Handle point coordinate input with rawInputs sync. */
/**
 * Fills the P1/P2 inputs from internal metre points, converted to the site unit.
 * The points themselves stay in metres so display rounding is not written back.
 */
function setPointRawInputs(points: {
	p1: { x: number; y: number; z: number };
	p2: { x: number; y: number; z: number };
}) {
	// Kept in metres: DistanceSettingInput converts for display.
	rawInputs.p1x = points.p1.x;
	rawInputs.p1y = points.p1.y;
	rawInputs.p1z = points.p1.z;
	rawInputs.p2x = points.p2.x;
	rawInputs.p2y = points.p2.y;
	rawInputs.p2z = points.p2.z;
}

// DistanceSettingInput emits internal metres, which is what the viewer and the
// gradient/length maths expect.
function onPointInput(
	which: "p1" | "p2",
	axis: "x" | "y" | "z",
	val: number | undefined | null,
) {
	const key: TDoublePointKey = `${which}${axis}`;
	if (val === undefined || val === null) {
		rawInputs[key] = undefined;
		return;
	}
	// Anything not finite keeps the previous value rather than clearing the field.
	if (!Number.isFinite(val)) return;
	rawInputs[key] = val;
	updatePoint(which, axis, val);
}

// ---------------------------------------------------------------------------
// Two-point structures: atomic viewer update with latest-wins coalescing.
// The form is the single source of truth while editing; the viewer applies the
// full target state in one shot (deterministic — same input, same position).
// Form inputs are never overwritten; only derived values are synced.
// ---------------------------------------------------------------------------
/**
 * Create a latest-wins coalesced applier: calls arriving while an apply is
 * in flight replace the pending input instead of being dropped, and the
 * loop applies the most recent one afterwards.
 */
function createCoalescedApplier<T, TPreservedField extends string>(
	buildInput: () => T,
	/** Invalid input never enters the queue: it must not supersede a valid
	 *  in-flight apply (its raw echo stays visible in the form, unapplied). */
	isApplicable: (input: T) => boolean,
	applyOne: (
		input: T,
		preservedField: TPreservedField | undefined,
		/** True when a newer call is queued — skip echo writes, it supersedes us */
		hasNewerPending: () => boolean,
	) => Promise<void>,
	afterSettled: (preservedField?: TPreservedField) => Promise<void>,
) {
	// The preserved field travels with its input so a later call arriving
	// mid-apply never inherits the first call's UI intent.
	let pending: { input: T; preservedField?: TPreservedField } | null = null;
	let running = false;

	/** Apply queued inputs until none remain (latest-wins). */
	async function drainPending(): Promise<TPreservedField | undefined> {
		let last: TPreservedField | undefined;
		// Per-iteration catch: a failed apply must not discard newer pending input
		while (pending) {
			const next = pending;
			pending = null;
			last = next.preservedField;
			try {
				await applyOne(next.input, next.preservedField, () => pending !== null);
			} catch (e: unknown) {
				console.error("Failed to apply two-point structure update:", e);
			}
		}
		return last;
	}

	return async (preservedField?: TPreservedField) => {
		const input = buildInput();
		if (!isApplicable(input)) return; // keep last valid state
		editGeneration++;
		pending = { input, preservedField };
		if (running) return;
		running = true;
		isUpdatingPoints.value = true;
		try {
			// drain → settle → repeat: input arriving while afterSettled is
			// awaiting would otherwise be stranded (running is still true)
			do {
				await afterSettled(await drainPending());
			} while (pending);
		} catch (e: unknown) {
			console.error("Failed to settle two-point structure update:", e);
		} finally {
			running = false;
			isUpdatingPoints.value = false;
		}
	};
}

// ── Retaining wall ──────────────────────────────────────────────────────────

function buildRetainingWallParamsFromForm(): TRetainingWallTwoPointParams {
	const p1 = doublePointData.points.p1;
	const p2 = doublePointData.points.p2;
	const topZ = p1.z;
	return {
		p1: { x: p1.x, y: p1.y, z: topZ },
		// Wall top edge is horizontal: P2 shares P1's Z
		p2: { x: p2.x, y: p2.y, z: topZ },
		heightM: Number(editData.dimensions.z) || 0,
		gradientPercent: roundTo3(Number(doublePointData.gradient?.value) || 0),
	};
}

/** Sync derived values (never input echoes) from the confirmed viewer state. */
function syncRetainingWallDerived(
	geometry: TGeometryResult,
	input: TRetainingWallTwoPointParams,
) {
	editData.dimensions.x = geometry.dimensions.x;
	editData.localAnchorPoint = { ...input.p1 };
	Object.assign(localAnchorPoint, input.p1);
	doublePointData.points.p2 = { ...doublePointData.points.p2, z: input.p1.z };
	const bottomZ = geometry.meta.bottomZ ?? input.p1.z - input.heightM;
	retainingWallBaseZ.value = {
		p1: bottomZ,
		p2: bottomZ,
	};
}

const applyRetainingWallFromForm = createCoalescedApplier<
	TRetainingWallTwoPointParams,
	"heading"
>(
	buildRetainingWallParamsFromForm,
	(next) => {
		const dx = next.p2.x - next.p1.x;
		const dy = next.p2.y - next.p1.y;
		// Degenerate segment or non-positive height: keep last valid state
		return Math.sqrt(dx * dx + dy * dy) >= 1e-8 && next.heightM > 0;
	},
	async (next, _preservedField, hasNewerPending) => {
		const sm = getSolidMesh();
		if (!sm?.isLoaded) return;
		const update = sm.updateRetainingWallFromTwoPoints as (
			params: TRetainingWallTwoPointParams,
		) => Promise<TGeometryResult | undefined>;
		const result = await update.call(sm, next);
		// A newer queued input supersedes this echo — writing it would clobber
		// the user's fresher form values with stale derived state
		if (result && !hasNewerPending()) syncRetainingWallDerived(result, next);
	},
	async (preservedField) => {
		// Heading display is a derived value — refresh unless the user is
		// editing the heading field itself (its echo must be preserved).
		if (preservedField === "heading") return;
		const sm = getSolidMesh();
		if (sm?.isLoaded) await syncHeadingAngle(sm.id as string);
	},
);

// ── Pipe ────────────────────────────────────────────────────────────────────

function buildPipeParamsFromForm(): TPipeTwoPointParams {
	const p1 = doublePointData.points.p1;
	const p2 = doublePointData.points.p2;
	return {
		p1: { x: p1.x, y: p1.y, z: p1.z },
		p2: { x: p2.x, y: p2.y, z: p2.z },
		diameterMm: Number(editData.dimensions.x) || 0,
	};
}

/** Sync derived values (never input echoes) from the confirmed viewer state. */
function syncPipeDerived(
	geometry: TGeometryResult,
	input: TPipeTwoPointParams,
	preservedField?: "heading" | "gradient" | "length",
) {
	if (preservedField !== "length") {
		editData.dimensions.z = geometry.dimensions.z;
	}
	editData.localAnchorPoint = { ...input.p1 };
	Object.assign(localAnchorPoint, input.p1);
	if (preservedField !== "gradient") {
		doublePointData.gradient.value = Number(
			pipeGradientPercentFromPoints(input.p1, input.p2).toFixed(2),
		);
	}
}

const applyPipeFromForm = createCoalescedApplier<
	TPipeTwoPointParams,
	"heading" | "gradient" | "length"
>(
	buildPipeParamsFromForm,
	(next) => {
		const dx = next.p2.x - next.p1.x;
		const dy = next.p2.y - next.p1.y;
		const dz = next.p2.z - next.p1.z;
		// Degenerate segment or non-positive diameter: keep last valid state
		return (
			Math.sqrt(dx * dx + dy * dy + dz * dz) >= 1e-8 && next.diameterMm > 0
		);
	},
	async (next, preservedField, hasNewerPending) => {
		const sm = getSolidMesh();
		if (!sm?.isLoaded) return;
		const update = sm.updatePipeFromTwoPoints as (
			params: TPipeTwoPointParams,
		) => Promise<TGeometryResult | undefined>;
		const result = await update.call(sm, next);
		// A newer queued input supersedes this echo (see wall applier note)
		if (result && !hasNewerPending())
			syncPipeDerived(result, next, preservedField);
	},
	async (preservedField) => {
		// Heading echo preserved while editing heading itself; a gradient edit
		// never changes the XY bearing — skip the resync in both cases so the
		// displayed azimuth is untouched (D3D-779 contract).
		if (preservedField === "heading" || preservedField === "gradient") return;
		const sm = getSolidMesh();
		if (sm?.isLoaded) await syncHeadingAngle(sm.id as string);
	},
);

/**
 * Basis points + baseline-relative bearing from the viewer's quaternion.
 * Euler-decomposition free: stays correct for steep/downward pipes where
 * relative HPR decomposition flips or corrupts the heading (D3D-779 class).
 */
async function getLocalEndpoints(
	meshId: string,
): Promise<TSolidMeshLocalEndpoints | null> {
	try {
		const axis =
			structureType.value === SIMPLE_STRUCTURE_TYPE.RETAINING_WALL ? "x" : "z";
		const result = (await store.dispatch(
			"solidMesh/getSolidMeshLocalEndpoints",
			{ meshId, axis },
		)) as TSolidMeshLocalEndpoints | null;
		if (result?.p1 && result?.p2) return result;
		console.warn(
			"[SolidMeshPanel] getSolidMeshLocalEndpoints returned no endpoints:",
			result,
		);
		return null;
	} catch (e: unknown) {
		// Callers degrade gracefully but the cause must stay diagnosable
		// (e.g. a stale window.viewer bundle missing the endpoints API)
		console.error("[SolidMeshPanel] getSolidMeshLocalEndpoints failed:", e);
		return null;
	}
}

async function syncHeadingAngle(meshId: string) {
	const gen = editGeneration;
	const endpoints = await getLocalEndpoints(meshId);
	// A newer user edit landed while awaiting — its echo owns the field now
	if (gen !== editGeneration) return;
	// Vertical axis: bearing undefined — keep the currently displayed value
	if (endpoints?.relativeBearingDeg == null) return;
	headingAngleDeg.value = normalizeHeadingInput(endpoints.relativeBearingDeg);
}

async function onHeadingChange(val: number | null | undefined) {
	if (val === null || val === undefined) return;
	const parsed = Number(val);
	if (!Number.isFinite(parsed)) return;

	const sm = getSolidMesh();
	if (!sm?.isLoaded || !props.isEditing) return;
	const prevHeading = headingAngleDeg.value;
	const nextHeading = normalizeHeadingInput(parsed);
	headingAngleDeg.value = nextHeading;
	if (prevHeading === nextHeading) return;
	const headingDelta = nextHeading - prevHeading;

	if (structureType.value === SIMPLE_STRUCTURE_TYPE.RETAINING_WALL) {
		// UI policy: heading edit rotates the P1→P2 segment about P1 by the
		// display-heading delta (coordinate math lives in the viewer helper)
		doublePointData.points.p2 = rotateSegmentXYByHeadingDelta(
			doublePointData.points.p1,
			doublePointData.points.p2,
			headingDelta,
		);
		await applyRetainingWallFromForm("heading");
		return;
	}

	if (structureType.value === SIMPLE_STRUCTURE_TYPE.PIPE) {
		// UI policy: heading edit rotates the horizontal component of P1→P2
		// about P1, keeping the vertical rise (math lives in the viewer helper)
		const p1 = doublePointData.points.p1;
		const p2 = doublePointData.points.p2;
		const hd = Math.hypot(p2.x - p1.x, p2.y - p1.y);
		if (hd < 1e-10) return; // vertical pipe: heading is meaningless
		doublePointData.points = {
			...doublePointData.points,
			p2: rotateSegmentXYByHeadingDelta(p1, p2, headingDelta),
		};
		await applyPipeFromForm("heading");
	}
}

async function syncCoordinatesFromViewer() {
	const sm = getSolidMesh();
	if (!sm?.isLoaded || !props.isEditing) return;
	await fetchLocalAnchorPoint();
	const prevP1 = { ...doublePointData.points.p1 };
	const prevP2 = { ...doublePointData.points.p2 };
	const p1: TPoint3 = {
		x: localAnchorPoint.x,
		y: localAnchorPoint.y,
		z: localAnchorPoint.z,
	};
	if (isDraggingSolidMesh.value) {
		editData.localAnchorPoint = {
			...editData.localAnchorPoint,
			x: p1.x,
			y: p1.y,
		};
	} else {
		editData.localAnchorPoint = { ...p1 };
	}
	if (isUpdatingPoints.value) return;

	// During drag, only follow X/Y in UI to avoid jitter from frequent re-computation.
	// Drag = translate: heading unchanged, P2 follows P1 by the same XY delta.
	if (isDraggingSolidMesh.value) {
		const { p1: newDragP1, p2: newDragP2 } = computeDragPoints(
			prevP1,
			prevP2,
			p1,
		);
		doublePointData.points.p1 = newDragP1;
		doublePointData.points.p2 = newDragP2;
		return;
	}

	// One snapshot: heading and P1/P2 must come from the same endpoint read
	const gen = editGeneration;
	const endpoints = await getLocalEndpoints(sm.id as string);
	if (!endpoints) return;
	// An edit may have started (or started AND settled) while awaiting —
	// the form is authoritative then
	if (isUpdatingPoints.value || gen !== editGeneration) return;
	if (
		structureType.value === SIMPLE_STRUCTURE_TYPE.RETAINING_WALL &&
		!retainingWallBaseZInitialized.value
	)
		return;
	if (endpoints.relativeBearingDeg != null) {
		headingAngleDeg.value = normalizeHeadingInput(endpoints.relativeBearingDeg);
	}
	if (structureType.value === SIMPLE_STRUCTURE_TYPE.RETAINING_WALL) {
		// Drag = translate (height unchanged): the base Z follows the wall
		const height = Number(editData.dimensions.z) || 0;
		retainingWallBaseZ.value = {
			p1: p1.z - height,
			p2: p1.z - height,
		};
	}
	doublePointData.points.p1 = { ...endpoints.p1 };
	doublePointData.points.p2 = { ...endpoints.p2 };
}

async function loadEditData() {
	await loadCommonEditData();
	const sm = getSolidMesh();
	if (!sm?.isLoaded) return;

	try {
		// One snapshot: heading and P1/P2 must come from the same endpoint read
		const gen = editGeneration;
		const endpoints = await getLocalEndpoints(sm.id as string);
		if (!endpoints) return;
		// A user edit landed while awaiting — the form is authoritative now
		if (gen !== editGeneration) return;
		if (endpoints.relativeBearingDeg != null) {
			headingAngleDeg.value = normalizeHeadingInput(
				endpoints.relativeBearingDeg,
			);
		}
		const p1: TPoint3 = {
			...endpoints.p1,
		};
		const p2: TPoint3 = {
			...endpoints.p2,
		};
		doublePointData.points.p1 = { ...p1 };
		doublePointData.points.p2 = { ...p2 };

		if (structureType.value === SIMPLE_STRUCTURE_TYPE.PIPE) {
			// Gradient from the endpoints themselves (sign-safe for downward pipes)
			const gPct = Number(
				pipeGradientPercentFromPoints(endpoints.p1, endpoints.p2).toFixed(2),
			);
			doublePointData.gradient.value = gPct;
			gradientDisplayPercent.value = gPct;
		} else {
			// Wall gradient is the cross-slope roll — not derivable from the
			// top-edge points. Safe via Euler here because wall pitch is always 0.
			const rotDeg = await getRotationDegrees(sm.id as string);
			const gPct = wallGradientPercentFromRollDeg(rotDeg.x);
			doublePointData.gradient.value = gPct;
			gradientDisplayPercent.value = gPct;
			const vertH = verticalHeightFromSlopeHeight(
				Number(scaledDimensions.value.z),
				gPct,
			);
			editData.dimensions.z = vertH;
			// Prefer immutable creation-time base from attributes.bottomZ.
			const attrs = sm.attributes as { bottomZ?: number } | undefined;
			const attrBaseZ = attrs?.bottomZ;
			const baseZ = Number.isFinite(attrBaseZ as number)
				? (attrBaseZ as number)
				: p1.z - vertH;
			retainingWallBaseZ.value = { p1: baseZ, p2: baseZ };
			retainingWallBaseZInitialized.value = true;
		}
	} catch (e: unknown) {
		console.error("Failed to build edit points:", e);
	}
	// Initialize rawInputs from loaded data
	initCommonRawInputs(rawInputs);
	setPointRawInputs(doublePointData.points);
	rawInputs.heading = headingAngleDeg.value;
	rawInputs.gradient = doublePointData.gradient.value;
}

async function onDimensionsChange() {
	const sm = getSolidMesh();
	if (!sm) return;
	const config = sm.shapeConfig as {
		dimensions: TPoint3;
		scale: TPoint3;
		shapeType: "box" | "sphere" | "cylinder";
	};
	const { dimensions, scale } = config;

	const xValue = editData.dimensions.x;
	const yValue = editData.dimensions.y;
	const zValue = editData.dimensions.z;

	if (structureType.value === SIMPLE_STRUCTURE_TYPE.RETAINING_WALL) {
		// Thickness (debug-only field) is not part of the two-point input —
		// apply it directly before the atomic update (which preserves scale.y)
		const currentThickness = dimensions.y * scale.y;
		if (Math.abs(yValue - currentThickness) > 1e-9) {
			(sm.setScale as (s: TPoint3) => void)({
				x: scale.x,
				y: yValue / dimensions.y,
				z: scale.z,
			});
		}

		const p1 = doublePointData.points.p1;
		const p2 = doublePointData.points.p2;
		// Height change keeps the bottom edge fixed: top edge Z follows.
		// Width change keeps P1 and the direction: P2 follows.
		const topZ = retainingWallBaseZ.value.p1 + Number(zValue);
		const newP2 = scaleSegmentToLength(
			{ ...p1, z: topZ },
			{ x: p2.x, y: p2.y, z: topZ },
			Number(xValue),
			config.shapeType,
		);
		doublePointData.points = {
			p1: { ...p1, z: topZ },
			p2: newP2,
		};
		await applyRetainingWallFromForm();
		return;
	}

	// Pipe: length change keeps P1 and the direction — scale P1→P2 to the new
	// length in form state, then apply atomically (diameter comes from the form)
	if (structureType.value === SIMPLE_STRUCTURE_TYPE.PIPE) {
		doublePointData.points = {
			...doublePointData.points,
			p2: scaleSegmentToLength(
				doublePointData.points.p1,
				doublePointData.points.p2,
				Number(zValue),
				config.shapeType,
			),
		};
		await applyPipeFromForm("length");
	}
}

async function onGradientChange(val: number | string | null | undefined) {
	// Both structure types apply via coalescers (latest-wins) — never drop inputs
	if (val === "" || val === null || val === undefined) return;
	const raw = Number(val);
	if (!Number.isFinite(raw)) return;
	// Clamp out-of-range input and keep applying to viewer to keep UI/model in sync.
	const gradientPct = clampGradientPercent(raw);
	if (gradientPct !== raw) {
		rawInputs.gradient = gradientPct;
	}
	if (structureType.value === SIMPLE_STRUCTURE_TYPE.RETAINING_WALL) {
		// Gradient is part of the two-point input — apply atomically from form
		doublePointData.gradient.value = gradientPct;
		await applyRetainingWallFromForm();
		return;
	}
	if (structureType.value === SIMPLE_STRUCTURE_TYPE.PIPE) {
		// UI policy: gradient edit keeps P1 and the 3D length; the new P2
		// derivation (bearing preservation) lives in the viewer helper
		const currentLength = Number(editData.dimensions.z);
		if (currentLength < 1e-10) return;
		doublePointData.points = {
			...doublePointData.points,
			p2: computePipeP2FromGradient(
				doublePointData.points.p1,
				doublePointData.points.p2,
				gradientPct,
				currentLength,
			),
		};
		await applyPipeFromForm("gradient");
	}
}

async function updatePoint(
	which: "p1" | "p2",
	axis: "x" | "y" | "z",
	val: number | string | null | undefined,
) {
	if (!doublePointData.points) return;
	const prevVal = doublePointData.points[which]?.[axis] ?? 0;
	const numVal =
		val === "" || val === null || val === undefined ? 0 : Number(val);
	doublePointData.points = {
		...doublePointData.points,
		[which]: {
			...doublePointData.points[which],
			[axis]: Number.isNaN(numVal) ? 0 : numVal,
		},
	};
	if (structureType.value === SIMPLE_STRUCTURE_TYPE.RETAINING_WALL) {
		if (axis === "z") {
			// Z edit = translate the whole wall (height unchanged):
			// top edge stays horizontal, so both points share the new Z
			const z = doublePointData.points[which].z;
			doublePointData.points = {
				p1: { ...doublePointData.points.p1, z },
				p2: { ...doublePointData.points.p2, z },
			};
		}
		await applyRetainingWallFromForm();
		return;
	}
	if (structureType.value === SIMPLE_STRUCTURE_TYPE.PIPE) {
		if (which === "p1" && axis !== "z") {
			// P1.x/y edit = translate the pipe: P2 follows by the same delta
			const delta = doublePointData.points.p1[axis] - prevVal;
			doublePointData.points = {
				...doublePointData.points,
				p2: {
					...doublePointData.points.p2,
					[axis]: doublePointData.points.p2[axis] + delta,
				},
			};
		}
		// P1.z edit keeps P2 fixed (gradient/length change);
		// P2 edits keep P1 fixed — both are already just form state
		await applyPipeFromForm();
	}
}

async function syncGradientFromReset(_resetRotation: unknown) {
	if (!props.isEditing || !solidMesh.value) return;
	const sm = getSolidMesh();
	if (structureType.value === SIMPLE_STRUCTURE_TYPE.RETAINING_WALL) {
		// Wall cross-slope roll: safe via Euler because wall pitch is always 0
		const rotDeg = await getRotationDegrees(sm.id as string);
		doublePointData.gradient.value = wallGradientPercentFromRollDeg(rotDeg.x);
	} else if (structureType.value === SIMPLE_STRUCTURE_TYPE.PIPE) {
		const endpoints = await getLocalEndpoints(sm.id as string);
		if (!endpoints) return;
		doublePointData.gradient.value = Number(
			pipeGradientPercentFromPoints(endpoints.p1, endpoints.p2).toFixed(2),
		);
	}
}

// ---------------------------------------------------------------------------
// External rotation sync (replaces the former 150ms rotation polling)
// SolidMeshObject.setRotation bumps a store stamp whenever any actor (e.g. the
// sibling transform panel) rotates the mesh; we resync heading and P2 then.
// ---------------------------------------------------------------------------
async function syncFromExternalRotation() {
	const sm = getSolidMesh();
	if (!sm?.isLoaded) return;
	if (!props.isEditing) return;
	if (isUpdatingPoints.value) return;

	// One snapshot: heading and P2 must come from the same endpoint read
	const gen = editGeneration;
	const endpoints = await getLocalEndpoints(sm.id as string);
	if (!endpoints) return;
	// An edit may have started (or started AND settled) while awaiting —
	// the form is authoritative then
	if (isUpdatingPoints.value || gen !== editGeneration) return;
	if (endpoints.relativeBearingDeg != null) {
		headingAngleDeg.value = normalizeHeadingInput(endpoints.relativeBearingDeg);
	}
	doublePointData.points = {
		...doublePointData.points,
		p2: { ...endpoints.p2 },
	};
}

watch(
	() =>
		store.getters[
			"solidMesh/solidMeshRotationStamp"
		] as TSolidMeshRotationStamp | null,
	async (stamp) => {
		if (!stamp || stamp.meshId !== props.solidMeshId) return;
		await syncFromExternalRotation();
	},
);

// ---------------------------------------------------------------------------
// Watchers
// ---------------------------------------------------------------------------

// solidMesh non-editing gradient display
async function syncGradientDisplay() {
	const sm = solidMesh.value as Record<string, unknown> | undefined;
	if (!sm || !sm.isLoaded) return;
	try {
		if (structureType.value === SIMPLE_STRUCTURE_TYPE.PIPE) {
			// Endpoint-derived gradient keeps its sign for downward pipes
			const endpoints = await getLocalEndpoints(sm.id as string);
			if (!endpoints) return;
			gradientDisplayPercent.value = Number(
				pipeGradientPercentFromPoints(endpoints.p1, endpoints.p2).toFixed(2),
			);
		} else if (structureType.value === SIMPLE_STRUCTURE_TYPE.RETAINING_WALL) {
			const rotDeg = await getRotationDegrees(sm.id as string);
			gradientDisplayPercent.value = wallGradientPercentFromRollDeg(rotDeg.x);
		}
	} catch (e) {
		console.warn("[SolidMeshPanel] failed to sync rotation/gradient:", e);
	}
}

watch(
	solidMesh,
	async (newVal) => {
		if (newVal && !props.isEditing) {
			await syncGradientDisplay();
		}
	},
	{ immediate: true },
);

// solidMesh.isLoaded
watch(
	() =>
		(solidMesh.value as Record<string, unknown> | undefined)?.isLoaded as
			| boolean
			| undefined,
	async (isLoaded) => {
		if (isLoaded) {
			await fetchLocalAnchorPoint();
			if (props.isEditing) {
				await loadEditData();
			} else {
				await syncGradientDisplay();
			}
		}
	},
	{ immediate: true },
);

// isEditing
watch(
	() => props.isEditing,
	async (newVal) => {
		if (newVal) {
			await loadEditData();
		} else {
			await syncGradientDisplay();
		}
	},
	{ immediate: true },
);

// wgs84Center
watch(
	() => {
		const sm = solidMesh.value as Record<string, unknown> | undefined;
		const config = sm?.shapeConfig as { wgs84Center?: TPoint3 } | undefined;
		return config?.wgs84Center;
	},
	() => {
		if (!props.isEditing || isUpdatingPosition.value || isUpdatingPoints.value)
			return;
		// Two-point structures: the form is authoritative while editing (viewer
		// state is derived from it) — read back only while a 3D drag is in
		// progress. Drag end is handled by the isDraggingSolidMesh watcher below.
		if (!isDraggingSolidMesh.value) return;
		if (anchorFetchTimeout.value) clearTimeout(anchorFetchTimeout.value);
		anchorFetchTimeout.value = setTimeout(() => {
			syncCoordinatesFromViewer();
		}, 0);
	},
	{ deep: true },
);

watch(
	() => isDraggingSolidMesh.value,
	async (dragging, prevDragging) => {
		if (prevDragging && !dragging && props.isEditing) {
			await syncCoordinatesFromViewer();
		}
	},
);

// --- rawInputs sync watchers ---
// Sync p1/p2 rawInputs when points change externally (drag, rotation, etc.)
watch(
	() => doublePointData.points,
	(pts) => {
		if (!props.isEditing) return;
		setPointRawInputs(pts);
	},
	{ deep: true },
);

// Sync heading rawInputs (guard: don't overwrite user-cleared empty field)
watch(headingAngleDeg, (val) => {
	if (!props.isEditing) return;
	if (rawInputs.heading === undefined) return;
	rawInputs.heading = val;
});

// Sync gradient rawInputs from model changes
watch(
	() => doublePointData.gradient.value,
	(val) => {
		if (!props.isEditing) return;
		rawInputs.gradient = val;
	},
);

// ---------------------------------------------------------------------------
// Expose for parent ref calls
// ---------------------------------------------------------------------------
defineExpose({ syncGradientFromReset });
</script>

<style scoped>
.solid-mesh-panel {
	margin-bottom: 8px;
	background-color: rgb(var(--v-theme-panel_mian));
	opacity: 1;
}

.solid-mesh-panel :deep(.v-card-text) {
	background-color: rgb(var(--v-theme-panel_main));
	max-height: calc(100vh - 200px);
	overflow-y: auto;
}

.solid-mesh-detail-view :deep(.v-card-text) {
	background-color: rgb(var(--v-theme-surface));
}

.color-preview {
	width: 20px;
	height: 20px;
	border: 1px solid #ccc;
	border-radius: 4px;
}

.cursor-pointer {
	cursor: pointer;
}

.text-caption {
	color: rgba(255, 255, 255, 0.7);
	font-weight: 500;
}

.text-body-2 {
	color: white;
}

.gap-2 {
	gap: 8px;
}

.field-stack {
	display: flex;
	flex-direction: column;
	gap: 8px;
	width: 100%;
}

.field-stack :deep(.v-input),
.field-stack :deep(.v-text-field),
.basis-point-group :deep(.v-input),
.basis-point-group :deep(.v-text-field) {
	width: 100%;
}

.basis-point-group {
	display: flex;
	flex-direction: column;
	gap: 8px;
	width: 100%;
}

.v-text-field :deep(.v-field) {
	font-size: 12px;
}

.v-text-field :deep(.v-field__input) {
	padding: 8px;
	min-height: 28px;
}

.dragging-locked :deep(.v-field) {
	background-color: rgba(100, 100, 100, 0);
}
</style>
